import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle } from "lucide-react";

interface QuoteResponseButtonsProps {
  docLabel: string;
  submitting: boolean;
  onApprove: () => void;
  onDecline: () => void;
  /** Side-by-side at every width, 44px tall — for the pinned strip. */
  compact?: boolean;
  children?: ReactNode;
}

// The customer's Approve / Decline pair, shared by the top card and the bottom
// response card on the public quote page so the two can't drift apart.
export function QuoteResponseButtons({ docLabel, submitting, onApprove, onDecline, compact, children }: QuoteResponseButtonsProps) {
  return (
    <div className={compact ? "flex flex-row gap-2" : "flex flex-col sm:flex-row gap-3"}>
      <Button
        onClick={onApprove}
        disabled={submitting}
        className={compact ? "flex-1 h-11" : "sm:flex-1"}
        size={compact ? "default" : "lg"}
      >
        <CheckCircle className="h-4 w-4 mr-2" />
        Approve {docLabel}
      </Button>
      <Button variant="outline" onClick={onDecline} disabled={submitting} className={compact ? "flex-1 h-11" : "sm:flex-1"}>
        <XCircle className="h-4 w-4 mr-2" />
        Decline
      </Button>
      {children}
    </div>
  );
}

// Customers kept missing the response card at the bottom of the public quote
// (#815), so an unanswered quote repeats Approve / Decline at the top, pinned
// under the sticky page header so it stays on screen while scrolling (#856).
// The bottom card stays: it also holds Request Revision and post-response states.
export function QuoteTopActions(props: Omit<QuoteResponseButtonsProps, "children" | "compact">) {
  const noun = props.docLabel.toLowerCase();
  return (
    <section
      aria-label={`Respond to this ${noun}`}
      className="no-print border-b border-border bg-background/95 backdrop-blur-sm"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <p className="hidden sm:block flex-1 text-sm font-medium">
          Ready to proceed? <span className="font-normal text-muted-foreground">Full details are below.</span>
        </p>
        <div className="w-full sm:w-auto">
          <QuoteResponseButtons {...props} compact />
        </div>
      </div>
    </section>
  );
}
